import React, { useMemo, useState } from 'react';
import { get, ref } from 'firebase/database';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { database } from '../firebase';

type ClickEvent = {
  id: string;
  button: string;
  projectId: string;
  timestamp: number;
};

type HourlyButtonNode = {
  button?: unknown;
  count?: unknown;
};

type HourlyButtonRow = {
  date: string;
  hour: string;
  bucket: string;
  buttonKey: string;
  button: string;
  count: number;
};

type ChartRow = {
  bucket: string;
  [buttonKey: string]: string | number;
};

type AnalyticsMode = 'clicks' | 'logs';
type LogSeverity = 'WARNING' | 'ERROR';

type LogEvent = {
  id: string;
  severity: string;
  message: string;
  timestamp: number;
  functionName: string;
};

const chartColors = [
  '#8b5cf6',
  '#22c55e',
  '#f97316',
  '#38bdf8',
  '#f43f5e',
  '#eab308',
  '#14b8a6',
  '#a855f7',
  '#ef4444',
  '#84cc16',
];

const getBogotaDateKey = (date: Date): string => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: 'America/Bogota',
  }).formatToParts(date);
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${values.year}-${values.month}-${values.day}`;
};

const getMonthStart = (dateKey: string): string => `${dateKey.slice(0, 8)}01`;

const formatDateTime = (timestamp: number): string => {
  if (!timestamp) {
    return '-';
  }
  return new Intl.DateTimeFormat('es-CO', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZone: 'America/Bogota',
  }).format(new Date(timestamp * 1000));
};

const getDateKeysInRange = (startDate: string, endDate: string): string[] => {
  if (!startDate || !endDate || startDate > endDate) {
    return [];
  }

  const [startYear, startMonth, startDay] = startDate.split('-').map(Number);
  const [endYear, endMonth, endDay] = endDate.split('-').map(Number);
  const current = new Date(Date.UTC(startYear, startMonth - 1, startDay));
  const end = new Date(Date.UTC(endYear, endMonth - 1, endDay));
  const keys: string[] = [];

  while (current.getTime() <= end.getTime()) {
    keys.push(current.toISOString().slice(0, 10));
    current.setUTCDate(current.getUTCDate() + 1);
  }

  return keys;
};

const getHourKeys = () => Array.from({ length: 24 }, (_, index) => String(index).padStart(2, '0'));

const getNumber = (value: unknown): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

const getText = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

const parseProjectEvents = (value: Record<string, unknown> | null): ClickEvent[] => {
  if (!value) {
    return [];
  }

  return Object.entries(value)
    .map(([id, rawEvent]) => {
      const event = (rawEvent ?? {}) as Record<string, unknown>;
      return {
        id,
        button: getText(event.button),
        projectId: getText(event.projectId),
        timestamp: getNumber(event.timestamp),
      };
    })
    .filter((event) => event.timestamp > 0)
    .sort((a, b) => a.timestamp - b.timestamp);
};

const parseLogEvents = (value: Record<string, unknown> | null): LogEvent[] => {
  if (!value) {
    return [];
  }

  return Object.entries(value)
    .map(([id, rawEvent]) => {
      const event = (rawEvent ?? {}) as Record<string, unknown>;
      return {
        id,
        severity: getText(event.severity).toUpperCase(),
        message: getText(event.message),
        timestamp: getNumber(event.timestamp),
        functionName: getText(event.function),
      };
    })
    .filter((event) => event.timestamp > 0)
    .sort((a, b) => b.timestamp - a.timestamp);
};

const getButtonLabel = (buttonKey: string, labels: Record<string, string>) => labels[buttonKey] || buttonKey;

const getButtonGroup = (buttonKey: string) => {
  const parts = buttonKey.split('_').filter(Boolean);
  if (parts.length < 2) {
    return buttonKey;
  }
  return parts.slice(0, 2).join('_');
};

function ClickAnalyticsSection() {
  const todayKey = getBogotaDateKey(new Date());
  const [activeMode, setActiveMode] = useState<AnalyticsMode>('clicks');
  const [projectId, setProjectId] = useState('');
  const [projectEvents, setProjectEvents] = useState<ClickEvent[]>([]);
  const [projectStatus, setProjectStatus] = useState('');
  const [isLoadingProjectEvents, setIsLoadingProjectEvents] = useState(false);
  const [startDate, setStartDate] = useState(getMonthStart(todayKey));
  const [endDate, setEndDate] = useState(todayKey);
  const [buttonGroupFilter, setButtonGroupFilter] = useState('all');
  const [buttonFilter, setButtonFilter] = useState('all');
  const [hourlyRows, setHourlyRows] = useState<HourlyButtonRow[]>([]);
  const [hourlyButtonLabels, setHourlyButtonLabels] = useState<Record<string, string>>({});
  const [hourlyStatus, setHourlyStatus] = useState('');
  const [isLoadingHourly, setIsLoadingHourly] = useState(false);
  const [logEvents, setLogEvents] = useState<LogEvent[]>([]);
  const [logSeverityFilter, setLogSeverityFilter] = useState<'all' | LogSeverity>('all');
  const [logFunctionFilter, setLogFunctionFilter] = useState('all');
  const [logStatus, setLogStatus] = useState('');
  const [isLoadingLogs, setIsLoadingLogs] = useState(false);

  const loadProjectEvents = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const normalizedProjectId = projectId.trim();
    if (!normalizedProjectId) {
      setProjectEvents([]);
      setProjectStatus('Ingresa un Project ID válido.');
      return;
    }

    setIsLoadingProjectEvents(true);
    setProjectStatus('');
    try {
      const snapshot = await get(ref(database, `ClickAnalytics/byProject/${normalizedProjectId}`));
      const events = parseProjectEvents(snapshot.val() as Record<string, unknown> | null);
      setProjectEvents(events);
      setProjectStatus(events.length ? '' : 'No hay clicks registrados para este proyecto.');
    } catch (error) {
      setProjectEvents([]);
      setProjectStatus('No se pudo cargar la secuencia de clicks.');
    } finally {
      setIsLoadingProjectEvents(false);
    }
  };

  const loadHourlyRows = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const dateKeys = getDateKeysInRange(startDate, endDate);
    if (!dateKeys.length) {
      setHourlyRows([]);
      setHourlyButtonLabels({});
      setHourlyStatus('Selecciona un rango de fechas válido.');
      return;
    }

    setIsLoadingHourly(true);
    setHourlyStatus('');
    try {
      const snapshots = await Promise.all(
        dateKeys.map(async (dateKey) => ({
          dateKey,
          value: (await get(ref(database, `ClickAnalytics/hourlyByButton/${dateKey}`))).val() as Record<
            string,
            Record<string, HourlyButtonNode>
          > | null,
        }))
      );
      const labels: Record<string, string> = {};
      const seenButtonKeys = new Set<string>();
      const countsByBucket = new Map<string, Map<string, number>>();

      snapshots.forEach(({ dateKey, value }) => {
        Object.entries(value ?? {}).forEach(([hourKey, hourValue]) => {
          Object.entries(hourValue ?? {}).forEach(([buttonKey, buttonValue]) => {
            seenButtonKeys.add(buttonKey);
            const label = getText(buttonValue?.button);
            if (label) {
              labels[buttonKey] = label;
            }
            const bucketKey = `${dateKey} ${hourKey.padStart(2, '0')}`;
            const count = getNumber(buttonValue?.count);
            const bucketCounts = countsByBucket.get(bucketKey) ?? new Map<string, number>();
            bucketCounts.set(buttonKey, (bucketCounts.get(buttonKey) ?? 0) + count);
            countsByBucket.set(bucketKey, bucketCounts);
          });
        });
      });

      const buttonKeys = Array.from(seenButtonKeys).sort((a, b) =>
        getButtonLabel(a, labels).localeCompare(getButtonLabel(b, labels))
      );
      const nextRows = dateKeys.flatMap((dateKey) =>
        getHourKeys().flatMap((hour) => {
          const bucket = `${dateKey} ${hour}:00`;
          const bucketCounts = countsByBucket.get(`${dateKey} ${hour}`) ?? new Map<string, number>();
          if (!buttonKeys.length) {
            return [];
          }
          return buttonKeys.map((buttonKey) => ({
            date: dateKey,
            hour,
            bucket,
            buttonKey,
            button: getButtonLabel(buttonKey, labels),
            count: bucketCounts.get(buttonKey) ?? 0,
          }));
        })
      );

      setHourlyRows(nextRows);
      setHourlyButtonLabels(labels);
      setButtonGroupFilter((current) =>
        current === 'all' || buttonKeys.some((buttonKey) => getButtonGroup(buttonKey) === current) ? current : 'all'
      );
      setButtonFilter((current) => (current === 'all' || buttonKeys.includes(current) ? current : 'all'));
      setHourlyStatus(nextRows.length ? '' : 'No hay clicks registrados en este rango.');
    } catch (error) {
      setHourlyRows([]);
      setHourlyButtonLabels({});
      setHourlyStatus('No se pudo cargar la serie de tiempo.');
    } finally {
      setIsLoadingHourly(false);
    }
  };

  const buttonOptions = useMemo(
    () => {
      const options = new Map<string, string>();
      hourlyRows.forEach((row) => {
        options.set(row.buttonKey, row.button);
      });
      return Array.from(options.entries())
        .map(([buttonKey, button]) => ({ buttonKey, button }))
        .sort((a, b) => a.button.localeCompare(b.button));
    },
    [hourlyRows]
  );

  const buttonGroupOptions = useMemo(
    () =>
      Array.from(new Set(buttonOptions.map((option) => getButtonGroup(option.buttonKey)))).sort((a, b) =>
        a.localeCompare(b)
      ),
    [buttonOptions]
  );

  const groupedButtonOptions = useMemo(
    () =>
      buttonGroupFilter === 'all'
        ? buttonOptions
        : buttonOptions.filter((option) => getButtonGroup(option.buttonKey) === buttonGroupFilter),
    [buttonGroupFilter, buttonOptions]
  );

  const visibleButtonKeys = useMemo(
    () => (buttonFilter === 'all' ? groupedButtonOptions.map((option) => option.buttonKey) : [buttonFilter]),
    [buttonFilter, groupedButtonOptions]
  );

  const chartRows = useMemo(() => {
    const byBucket = new Map<string, ChartRow>();
    hourlyRows.forEach((row) => {
      if (!visibleButtonKeys.includes(row.buttonKey)) {
        return;
      }
      const current = byBucket.get(row.bucket) ?? { bucket: row.bucket };
      current[row.buttonKey] = row.count;
      byBucket.set(row.bucket, current);
    });
    return Array.from(byBucket.values()).sort((a, b) => String(a.bucket).localeCompare(String(b.bucket)));
  }, [hourlyRows, visibleButtonKeys]);
  const chartHeight = 360 + Math.max(0, visibleButtonKeys.length - 1) * 28;

  const loadLogEvents = async () => {
    setIsLoadingLogs(true);
    setLogStatus('');
    try {
      const snapshot = await get(ref(database, 'LogAnalytics/events'));
      const events = parseLogEvents(snapshot.val() as Record<string, unknown> | null);
      setLogEvents(events);
      setLogFunctionFilter((current) =>
        current === 'all' || events.some((event) => event.functionName === current) ? current : 'all'
      );
      setLogStatus(events.length ? '' : 'No hay eventos de backend registrados.');
    } catch (error) {
      setLogEvents([]);
      setLogStatus('No se pudieron cargar los errores de backend.');
    } finally {
      setIsLoadingLogs(false);
    }
  };

  const logFunctionOptions = useMemo(
    () =>
      Array.from(new Set(logEvents.map((event) => event.functionName).filter(Boolean))).sort((a, b) =>
        a.localeCompare(b)
      ),
    [logEvents]
  );

  const visibleLogEvents = useMemo(
    () =>
      logEvents.filter((event) => {
        if (logSeverityFilter !== 'all' && event.severity !== logSeverityFilter) {
          return false;
        }
        if (logFunctionFilter !== 'all' && event.functionName !== logFunctionFilter) {
          return false;
        }
        return true;
      }),
    [logEvents, logFunctionFilter, logSeverityFilter]
  );

  return (
    <div className="section">
      <div className="section-header">
        <h2>Analíticas</h2>
      </div>

      <div className="analytics-tabs">
        <button
          className={`secondary-button analytics-tab ${activeMode === 'clicks' ? 'analytics-tab-active' : ''}`}
          type="button"
          onClick={() => setActiveMode('clicks')}
        >
          Clicks usuarios
        </button>
        <button
          className={`secondary-button analytics-tab ${activeMode === 'logs' ? 'analytics-tab-active' : ''}`}
          type="button"
          onClick={() => setActiveMode('logs')}
        >
          Errores backend
        </button>
      </div>

      {activeMode === 'clicks' ? (
        <>
          <div className="click-analytics-block">
        <div className="section-header">
          <h3>Secuencia por proyecto</h3>
        </div>
        <form className="project-search" onSubmit={loadProjectEvents}>
          <label className="field">
            <span>Project ID</span>
            <input
              type="text"
              value={projectId}
              onChange={(event) => setProjectId(event.target.value)}
              placeholder="ID del proyecto"
            />
          </label>
          <button className="primary-button" type="submit" disabled={isLoadingProjectEvents}>
            {isLoadingProjectEvents ? 'Cargando...' : 'Buscar'}
          </button>
          {projectStatus ? <div className="inline-toast inline-toast-neutral">{projectStatus}</div> : null}
        </form>

        {projectEvents.length ? (
          <div className="table click-project-table">
            <div className="table-row table-header click-project-row">
              <div>#</div>
              <div>Fecha Bogota</div>
              <div>Botón</div>
            </div>
            {projectEvents.map((event, index) => (
              <div className="table-row click-project-row" key={event.id}>
                <div>{index + 1}</div>
                <div>{formatDateTime(event.timestamp)}</div>
                <div>{event.button || '-'}</div>
              </div>
            ))}
          </div>
        ) : null}
          </div>

          <div className="click-analytics-block">
        <div className="section-header">
          <h3>Serie de tiempo por botón</h3>
        </div>
        <form className="metrics-controls" onSubmit={loadHourlyRows}>
          <div className="metrics-control-row metrics-control-row-two">
            <label className="field">
              <span>Fecha inicial</span>
              <input type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} />
            </label>
            <label className="field">
              <span>Fecha final</span>
              <input type="date" value={endDate} onChange={(event) => setEndDate(event.target.value)} />
            </label>
          </div>
          <div className="metrics-control-row metrics-control-row-two">
            <label className="field">
              <span>Grupo</span>
              <select
                value={buttonGroupFilter}
                onChange={(event) => {
                  const nextGroup = event.target.value;
                  setButtonGroupFilter(nextGroup);
                  setButtonFilter((current) => {
                    if (current === 'all') {
                      return current;
                    }
                    return nextGroup === 'all' || getButtonGroup(current) === nextGroup ? current : 'all';
                  });
                }}
              >
                <option value="all">Todos los grupos</option>
                {buttonGroupOptions.map((group) => (
                  <option value={group} key={group}>
                    {group}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Botón</span>
              <select value={buttonFilter} onChange={(event) => setButtonFilter(event.target.value)}>
                <option value="all">Todos los botones</option>
                {groupedButtonOptions.map((option) => (
                  <option value={option.buttonKey} key={option.buttonKey}>
                    {option.button}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="metrics-control-row metrics-control-row-two">
            <div className="metrics-load-action">
              <button className="primary-button" type="submit" disabled={isLoadingHourly}>
                {isLoadingHourly ? 'Cargando...' : 'Cargar serie'}
              </button>
            </div>
          </div>
          {hourlyStatus ? <div className="inline-toast inline-toast-neutral">{hourlyStatus}</div> : null}
        </form>

        {chartRows.length ? (
            <div className="click-chart-wrap">
              <ResponsiveContainer width="100%" height={chartHeight}>
                <LineChart data={chartRows} margin={{ top: 12, right: 20, bottom: 8, left: 0 }}>
                  <CartesianGrid stroke="rgba(255,255,255,0.12)" vertical={false} />
                  <XAxis
                    dataKey="bucket"
                    stroke="#c7c7d1"
                    minTickGap={32}
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis allowDecimals={false} stroke="#c7c7d1" tick={{ fontSize: 12 }} />
                  <Tooltip
                    contentStyle={{
                      background: '#111111',
                      border: '1px solid rgba(255,255,255,0.18)',
                      borderRadius: 12,
                      color: '#ffffff',
                    }}
                    labelStyle={{ color: '#ffffff' }}
                  />
                  <Legend />
                  {visibleButtonKeys.map((buttonKey, index) => (
                    <Line
                      type="monotone"
                      dataKey={buttonKey}
                      name={getButtonLabel(buttonKey, hourlyButtonLabels)}
                      stroke={chartColors[index % chartColors.length]}
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4 }}
                      key={buttonKey}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
        ) : null}
          </div>
        </>
      ) : null}

      {activeMode === 'logs' ? (
        <div className="click-analytics-block">
          <div className="section-header">
            <h3>Errores backend</h3>
          </div>
          <div className="metrics-controls">
            <div className="metrics-control-row metrics-control-row-two">
              <label className="field">
                <span>Severity</span>
                <select
                  value={logSeverityFilter}
                  onChange={(event) => setLogSeverityFilter(event.target.value as 'all' | LogSeverity)}
                >
                  <option value="all">Todos</option>
                  <option value="ERROR">ERROR</option>
                  <option value="WARNING">WARNING</option>
                </select>
              </label>
              <label className="field">
                <span>Function</span>
                <select value={logFunctionFilter} onChange={(event) => setLogFunctionFilter(event.target.value)}>
                  <option value="all">Todas</option>
                  {logFunctionOptions.map((functionName) => (
                    <option value={functionName} key={functionName}>
                      {functionName}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="metrics-load-action">
              <button className="primary-button" type="button" onClick={loadLogEvents} disabled={isLoadingLogs}>
                {isLoadingLogs ? 'Cargando...' : 'Cargar errores'}
              </button>
            </div>
            {logStatus ? <div className="inline-toast inline-toast-neutral">{logStatus}</div> : null}
          </div>

          {visibleLogEvents.length ? (
            <div className="table click-log-table">
              <div className="table-row table-header click-log-row">
                <div>Fecha Bogota</div>
                <div>Severity</div>
                <div>Function</div>
                <div>Message</div>
                <div>Event ID</div>
              </div>
              {visibleLogEvents.map((event) => (
                <div
                  className={`table-row click-log-row click-log-row-${event.severity.toLowerCase()}`}
                  key={event.id}
                >
                  <div>{formatDateTime(event.timestamp)}</div>
                  <div className={`click-log-severity click-log-severity-${event.severity.toLowerCase()}`}>
                    {event.severity || '-'}
                  </div>
                  <div className="click-analytics-mono">{event.functionName || '-'}</div>
                  <div className="table-description">{event.message || '-'}</div>
                  <div className="click-analytics-mono">{event.id}</div>
                </div>
              ))}
            </div>
          ) : logEvents.length ? (
            <div className="table-placeholder">No hay eventos que coincidan con los filtros.</div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export default ClickAnalyticsSection;
